import React, {useState, useMemo} from 'react';
import {useTranslation, Trans} from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {useTheme} from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorRoundedIcon from '@mui/icons-material/ClearRounded';
import {IChangePasswordData, IPasswordData} from 'types/PasswordData';

type Props = {
   onSubmit: (data: any) => void;
   passwordData: IPasswordData;
   fromUserAdmin?: boolean;
   onClose: () => void;
};

export default function ChangePasswordForm({
   onSubmit,
   passwordData,
   fromUserAdmin,
   onClose,
}: Props) {
   const {t} = useTranslation();

   const [currentPassword, setCurrentPassword] = useState('');
   const [newPassword, setNewPassword] = useState('');
   const [newPassword2, setNewPassword2] = useState('');

   const validation = useMemo(() => {
      const counter = {
         upperCount: (newPassword.match(/[A-Z]/g) || []).length,
         lowerCount: (newPassword.match(/[a-z]/g) || []).length,
         numericCount: (newPassword.match(/[0-9]/g) || []).length,
         specialCount: (newPassword.match(/[^A-Za-z0-9]/g) || []).length,
      };

      const flags = {
         isCurrentDoNotMatch: currentPassword !== newPassword,
         isNewMatched: newPassword === newPassword2,
         length: newPassword.length >= passwordData.minNumChars,
         upper: counter.upperCount >= passwordData.minNumUpper,
         lower: counter.lowerCount >= passwordData.minNumLower,
         numeric: counter.numericCount >= passwordData.minNumNumeric,
         special: counter.specialCount >= passwordData.minNumSpecial,
      };

      return {
         ...flags,
         counter,
         isValid: newPassword2 === newPassword && Object.values(flags).every(Boolean),
      };
   }, [currentPassword, newPassword, newPassword2, passwordData, fromUserAdmin]);

   const handleSubmit = () => {
      if (!validation.isValid) return;

      onSubmit({
         currentPassword,
         newPassword,
      } as IChangePasswordData);
   };

   return (
      <>
         <Grid container spacing={2}>
            <Grid item xs={6}>
               {!fromUserAdmin && (
                  <TextField
                     fullWidth
                     type="password"
                     label={t('Current Password')}
                     value={currentPassword}
                     onChange={(e) => setCurrentPassword(e.target.value)}
                  />
               )}

               <TextField
                  fullWidth
                  type="password"
                  label={t('New Password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
               />

               <TextField
                  fullWidth
                  type="password"
                  label={t('Confirm New Password')}
                  value={newPassword2}
                  onChange={(e) => setNewPassword2(e.target.value)}
               />
            </Grid>

            <Grid item xs={6}>
               <ValidationEntry
                  valid={validation.length}
                  label={t('Must have at least {{minNumChars}} characters', {
                     minNumChars: passwordData.minNumChars,
                  })}
               />
               <ValidationEntry
                  valid={validation.isCurrentDoNotMatch}
                  label={t('Cannot match current password')}
               />
               <ValidationEntry
                  valid={validation.isNewMatched}
                  label={t('Confirmation must match')}
               />
               <Grid container>
                  <Grid item sm={6}>
                     <ValidationEntry
                        valid={validation.upper}
                        label={t('Upper case: {{r}} ({{n}})', {
                           r: validation.counter.upperCount,
                           n: passwordData.minNumUpper,
                        })}
                     />
                     <ValidationEntry
                        valid={validation.lower}
                        label={t('Lower case: {{r}} ({{n}})', {
                           r: validation.counter.lowerCount,
                           n: passwordData.minNumLower,
                        })}
                     />
                  </Grid>
                  <Grid item sm={6}>
                     <ValidationEntry
                        valid={validation.numeric}
                        label={t('Numbers: {{r}} ({{n}})', {
                           r: validation.counter.numericCount,
                           n: passwordData.minNumNumeric,
                        })}
                     />
                     <ValidationEntry
                        valid={validation.special}
                        label={t('Special char: {{r}} ({{n}})', {
                           r: validation.counter.specialCount,
                           n: passwordData.minNumSpecial,
                        })}
                     />
                  </Grid>
               </Grid>
            </Grid>
         </Grid>

         <Box mt={2} display="flex" gap={2} justifyContent="center">
            <Button
               variant="contained"
               color="primary"
               onClick={handleSubmit}
               disabled={!validation.isValid}
            >
               {t('Save')}
            </Button>
            <Button onClick={onClose}>{t('Cancel')}</Button>
         </Box>
      </>
   );
}

function ValidationEntry({valid, label}: any) {
   const theme = useTheme();
   const Icon = valid ? CheckRoundedIcon : ErrorRoundedIcon;

   return (
      <Box display="flex" alignItems="center" gap={1}>
         <Icon
            sx={{
               color: valid ? theme.palette.success.main : theme.palette.error.main,
            }}
         />
         <Typography>{label}</Typography>
      </Box>
   );
}
